export type ButtonVariant =
  "solid" | "solid-inverse" | "solid-inverse-quiet" | "outline";
export type ButtonSize = "md" | "sm" | "sentence";

/**
 * State colours come from Figma `CTAs` (`5289:2070`) — Primary Button (Dark)
 * for `solid`, Secondary Button for `outline`.
 *
 * Figma draws On Click identical to Hover for every variant in that frame, so
 * the pressed state carries no colour of its own: `active:` mirrors `hover:`
 * on purpose, and the press is told apart by scale alone (see [Button]).
 *
 * Disabled dims the label to 50% while the edge or fill behind it stays at
 * full strength, which is why the half-strength step is a `tint` token rather
 * than an opacity on the whole control.
 */
export const BUTTON_VARIANT_CLASSES: Record<ButtonVariant, string> = {
  solid: [
    "bg-content-primary text-content-inverse",
    "hover:bg-brand-white hover:text-content-primary hover:ring-1 hover:ring-inset hover:ring-content-primary",
    "active:bg-brand-white active:text-content-primary active:ring-1 active:ring-inset active:ring-content-primary",
    // Figma's disabled is the outline shell alone: no fill, neutral-2 edge.
    "disabled:bg-transparent disabled:ring-1 disabled:ring-inset disabled:ring-neutral2",
    "disabled:text-tint-neutral2-50",
  ].join(" "),
  /**
   * Figma's Primary Button (Light) (`9945:49304`) — the white button the hero
   * lays over media. Named `-inverse` rather than `-light`: this file's
   * neighbour already spends `-light` on "as it reads on a light surface"
   * (IconButtonVariant's `secondary-light`), and this is the opposite — a
   * light button for dark ground.
   *
   * A clean solid inversion: ink fill, white label, nothing outlining it.
   *
   * Figma draws a 1px brand-white edge on Hover and On Click. We leave it off
   * on purpose — reviewed over the hero video, the outlined version reads
   * wrong, and the fill alone already carries the state. Disabled keeps its
   * edge, which has no fill behind it to do that job.
   */
  "solid-inverse": [
    "bg-brand-white text-brand-black",
    "hover:bg-brand-black hover:text-brand-white",
    "active:bg-brand-black active:text-brand-white",
    "disabled:bg-transparent disabled:ring-1 disabled:ring-inset disabled:ring-tint-white-70",
    "disabled:text-tint-white-50",
  ].join(" "),
  /**
   * The white button /serene lays over its films: `solid-inverse`'s fill with
   * no hover swap, so it holds still over moving footage, and the slower
   * `medium` settle on the press.
   */
  "solid-inverse-quiet": [
    "bg-brand-white text-brand-black duration-medium",
    "disabled:bg-transparent disabled:ring-1 disabled:ring-inset disabled:ring-tint-white-70",
    "disabled:text-tint-white-50",
  ].join(" "),
  outline: [
    // Figma's resting edge is tints/black-20%, not the heavier `stroke`.
    "border border-tint-black-20 bg-transparent text-content-primary",
    "hover:border-transparent hover:bg-content-primary hover:text-content-inverse",
    "active:border-transparent active:bg-content-primary active:text-content-inverse",
    // ...and disabled fills, where `solid` empties out.
    "disabled:border-transparent disabled:bg-neutral2",
    "disabled:text-tint-white-50",
  ].join(" "),
};

/** The label's face rides with the size: the mono capitals, or `sentence`'s
    Mori in sentence case at body size. */
export const BUTTON_SIZE_CLASSES: Record<ButtonSize, string> = {
  md: "h-button px-xl font-trimMono font-medium uppercase text-eyebrow-md",
  sm: "px-l py-s font-trimMono font-medium uppercase text-eyebrow-sm",
  sentence: "px-l py-s font-ppMori font-normal text-paragraph-md",
};
